import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { BookingBasket } from '../components/BookingBasket';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { SubscriptionPlanOffcanvas } from '../components/SubscriptionPlanOffcanvas';
import {
  clearBookingDraft,
  getBookingDraft,
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import {
  createOneOffCheckout,
  createSubscriptionCheckout,
  listAddons,
  listCategories,
  listServices,
  type ServiceAddonRow,
  type ServiceCategory,
  type ServiceItem,
} from '../../lib/booking-api';
import {
  customerApiErrorMessage,
  getMyWallet,
  startMembershipCheckout,
} from '../../lib/customer-api';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';
import { trackInitiateCheckout } from '../../lib/analytics';

export default function ContinueBookingPage() {
  const navigate = useNavigate();
  const draft = useBookingDraft();

  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Meta Pixel `InitiateCheckout` — this whole page IS the checkout, so fire
  // once the server quote's total is known, with the booked service ids.
  const [checkoutTotal, setCheckoutTotal] = useState<number | null>(null);
  const initiateCheckoutFired = useRef(false);
  useEffect(() => {
    if (initiateCheckoutFired.current || checkoutTotal == null) return;
    initiateCheckoutFired.current = true;
    const ids = new Set<string>();
    if (draft?.service_id) ids.add(draft.service_id);
    for (const it of (draft?.items ?? []) as Array<{
      service_id?: string | null;
    }>) {
      if (it?.service_id) ids.add(it.service_id);
    }
    trackInitiateCheckout({
      value: checkoutTotal,
      currency: 'GBP',
      contentIds: [...ids],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutTotal]);

  // Wallet — same wiring as BookingFlowPage / YourAppointmentPage so the
  // "Use my wallet balance" toggle shows on this checkout too (one-off only;
  // the basket hides it for subscriptions). The chosen amount persists to the
  // draft, which the checkout below already feeds into wallet_amount.
  const { isAuthenticated: isCustomerAuthed } = useCustomerAuth();
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletCurrency, setWalletCurrency] = useState('GBP');
  const [walletAmount, setWalletAmount] = useState<number>(() =>
    Number(getBookingDraft()?.wallet_amount ?? 0),
  );
  const [walletEnabled, setWalletEnabled] = useState<boolean>(
    () => Number(getBookingDraft()?.wallet_amount ?? 0) > 0,
  );
  useEffect(() => {
    if (!isCustomerAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyWallet();
        if (cancelled) return;
        setWalletBalance(Number(res?.balance ?? 0));
        setWalletCurrency(res?.currency ?? 'GBP');
      } catch {
        /* non-blocking — basket just won't show the wallet toggle */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCustomerAuthed]);

  // SubscriptionPlanOffcanvas's "Pick Plan" button no-ops without a
  // handler — without this wiring the customer would tap Pick Plan
  // and see nothing happen (audit 2026-06-04). Mirror the
  // BookingFlowPage pattern: close the offcanvas first (so any error
  // toast is visible above the panel), then kick off a Stripe
  // membership checkout and redirect. Cycle defaults to MONTHLY —
  // the offcanvas doesn't expose a toggle yet.
  const handlePickPlan = async (planId: string) => {
    setIsSubscriptionOpen(false);
    try {
      const res = await startMembershipCheckout({
        subscription_id: planId,
        subscription_type: 'MONTHLY',
      });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      toast.error('Could not start checkout. Please try again.');
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not start checkout. Please try again.'),
      );
    }
  };

  // Catalog lookup — categories + services are public, so we fetch
  // on mount and use them to map the wizard's code-ish state to real
  // FK ids the backend wants. Cheap calls; fine to do every visit.
  const [catalogReady, setCatalogReady] = useState(false);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [addonsCatalog, setAddonsCatalog] = useState<ServiceAddonRow[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [cats, svcs] = await Promise.all([
          listCategories(),
          listServices(),
        ]);
        setCategories(cats.data);
        setServices(svcs.data);
      } catch (err) {
        // Even if the catalog fetch fails we let the user click —
        // the backend will reject with a clear "service not found"
        // and we surface that.
        console.warn('Failed to load catalog', err);
      } finally {
        setCatalogReady(true);
      }
    })();
  }, []);

  // Load add-ons for the booking's category so the basket can render
  // the same line items + total the /booking page showed.
  useEffect(() => {
    const categoryId = draft?.service_category_id;
    if (!categoryId) return;
    (async () => {
      try {
        const res = await listAddons(categoryId);
        setAddonsCatalog(res.data ?? []);
      } catch {
        /* basket renders fine without add-on labels */
      }
    })();
  }, [draft?.service_category_id]);

  // Resolve the service + category the customer just walked through.
  // Priority: explicit ids on the draft → ids inferred from the
  // category/service code → first ACTIVE service as a last resort.
  const resolved = useMemo(() => {
    if (!catalogReady) return null;
    // BookingFlowPage now writes draft.service_id explicitly. The
    // fall-throughs (service_code contains-match, first ACTIVE
    // service) exist for the direct-nav case where the customer hits
    // /continue-booking without having walked the wizard.
    let svc: ServiceItem | undefined;
    if (draft?.service_id) {
      svc = services.find((s) => s.id === draft.service_id);
    }
    if (!svc) {
      const desiredCode = (draft?.meta?.service_code as string) || '';
      if (desiredCode) {
        svc = services.find(
          (s) => s.code.toLowerCase() === desiredCode.toLowerCase(),
        );
      }
    }
    // No fallback to a hardcoded code — if the draft doesn't pin a
    // service we leave it null and let the page render its "missing
    // booking details" empty state instead of silently defaulting
    // the customer into the seeded cleaning service.
    let cat: ServiceCategory | undefined;
    if (svc?.category?.id) {
      cat = categories.find((c) => c.id === svc!.category.id);
    } else if (draft?.service_category_id) {
      cat = categories.find((c) => c.id === draft!.service_category_id);
    }
    return { service: svc, category: cat };
  }, [catalogReady, draft, categories, services]);

  // Pull basket display values from the live draft. The basket
  // handles empty / missing fields itself — we no longer pass mock
  // fallbacks ("Saturday 7 Mar", "£21", "11:00, 14:30") here because
  // the basket would render those literally instead of hinting at
  // missing data.
  const basketPrice = Number(resolved?.service?.base_price ?? 0);
  // Compute hours from the draft so the basket's parseDuration sees a
  // plain number it recognises ("4", "4.5") regardless of how the
  // admin keyed the category's duration_hours flow_config.
  const basketHours = useMemo(() => {
    const fromDraft = draft?.duration_minutes;
    if (typeof fromDraft === 'number' && fromDraft > 0) return fromDraft / 60;
    const fromMeta = draft?.meta?.hours;
    if (typeof fromMeta === 'number' && fromMeta > 0) return fromMeta;
    return (resolved?.service?.duration_minutes ?? 60) / 60;
  }, [draft?.duration_minutes, draft?.meta?.hours, resolved?.service?.duration_minutes]);
  const basketDuration = String(basketHours);
  const basketHasPets =
    typeof draft?.meta?.has_pets === 'string' ? draft.meta.has_pets : '';
  const basketAppointmentDate: Date | null = draft?.scheduled_start
    ? new Date(draft.scheduled_start)
    : null;
  const basketAppointmentTime = draft?.scheduled_start
    ? new Date(draft.scheduled_start).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        // Show the UK wall-clock time the slot was booked for — not the
        // browser's local zone (which shifts it on a non-UK device).
        timeZone: 'Europe/London',
      })
    : (draft?.start_time?.slice(0, 5) ?? '');
  const basketTimes: string[] = Array.isArray(draft?.meta?.times)
    ? draft.meta.times
    : [];
  const basketCategoryName = resolved?.category?.name ?? '';
  const basketAddons = Array.isArray(draft?.meta?.addons)
    ? (draft.meta.addons as { id: string; duration: string }[])
    : [];
  const basketCommercialSpace =
    typeof draft?.meta?.commercial_space === 'string'
      ? draft.meta.commercial_space
      : undefined;
  const basketCommercialOptions = useMemo(() => {
    return (resolved?.category?.flow_config?.commercial_spaces ?? []) as Array<{
      id: string;
      label: string;
    }>;
  }, [resolved?.category]);
  const basketOtherCommercial =
    typeof draft?.meta?.commercial_space_other === 'string'
      ? draft.meta.commercial_space_other
      : undefined;

  async function handlePayClick() {
    setSubmitting(true);
    try {
      const liveDraft = getBookingDraft();
      if (!liveDraft) {
        throw new Error('Your basket has expired — please start again.');
      }
      if (!resolved?.category || !resolved?.service) {
        throw new Error(
          'Could not resolve the service catalog — please refresh and retry.',
        );
      }

      // Address comes from the booking draft (set in the wizard / address
      // step). The auth user no longer carries an inline address — saved
      // addresses live in the customer_addresses book and are written into
      // the draft when chosen — so there's nothing to fall back to here.
      // Backend rejects without an address_line1.
      const address = liveDraft.address ?? null;

      if (!address?.address_line1) {
        throw new Error(
          'Please add a service address before paying.',
        );
      }

      // BookingFlowPage now writes real items[] with service +
      // service_addon ids. The fallback synth-line only fires on
      // direct nav (no wizard draft).
      const items =
        liveDraft.items && liveDraft.items.length > 0
          ? liveDraft.items
          : [
              {
                service_id: resolved.service.id,
                quantity: liveDraft.meta?.hours
                  ? Number(liveDraft.meta.hours)
                  : 3,
                properties: liveDraft.meta ?? null,
              },
            ];

      if (liveDraft.mode === 'SUBSCRIPTION') {
        // Daily runs every day, so it has no day_of_week — only the other
        // cadences require one.
        const needsDay = liveDraft.frequency !== 'DAILY';
        if (
          liveDraft.frequency === undefined ||
          (needsDay && liveDraft.day_of_week === undefined) ||
          !liveDraft.start_time
        ) {
          throw new Error(
            'Please pick a frequency, day and time before paying.',
          );
        }
        const res = await createSubscriptionCheckout({
          service_category_id: resolved.category.id,
          service_id: resolved.service.id,
          preferred_professional_id: liveDraft.professional_id ?? null,
          frequency: liveDraft.frequency,
          day_of_week: liveDraft.day_of_week,
          start_time: liveDraft.start_time,
          // Forward the IANA timezone captured at schedule-pick time
          // (or fall back to the customer's current browser zone).
          // Backend's spawner uses this to compute scheduled_start in
          // the customer's local clock instead of UTC.
          timezone:
            liveDraft.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          duration_minutes: liveDraft.duration_minutes,
          ...address,
          items,
          customer_note: liveDraft.customer_note ?? null,
          customer_note_images: liveDraft.customer_note_images ?? null,
          currency: liveDraft.currency ?? resolved.service.currency,
          accept_backup: liveDraft.meta?.substitute_preference !== 'no',
          secondary_professional_ids: Array.isArray(
            liveDraft.meta?.secondary_professional_ids,
          )
            ? (liveDraft.meta?.secondary_professional_ids as string[])
            : [],
        });
        if (res.checkout_url) {
          window.location.href = res.checkout_url;
          return;
        }
        // Backend can't return a no-payment branch for subs today;
        // if it ever does (e.g. trial), navigate to the post-redirect
        // confirmation with a synthetic session id.
        navigate('/booking/confirmation');
        return;
      }

      // One-off booking.
      const scheduledStart =
        liveDraft.scheduled_start ?? defaultScheduledStart();
      const res = await createOneOffCheckout({
        service_category_id: resolved.category.id,
        service_id: resolved.service.id,
        professional_id: liveDraft.professional_id ?? null,
        scheduled_start: scheduledStart,
        duration_minutes: liveDraft.duration_minutes,
        ...address,
        items,
        customer_voucher_id: liveDraft.customer_voucher_id ?? null,
        wallet_amount: liveDraft.wallet_amount ?? 0,
        customer_note: liveDraft.customer_note ?? null,
        currency: liveDraft.currency ?? resolved.service.currency,
        accept_backup: liveDraft.meta?.substitute_preference !== 'no',
        secondary_professional_ids: Array.isArray(
          liveDraft.meta?.secondary_professional_ids,
        )
          ? (liveDraft.meta?.secondary_professional_ids as string[])
          : [],
      });

      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      // Wallet covered everything — backend already confirmed.
      clearBookingDraft();
      navigate(`/bookings/${res.id}`);
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not start checkout.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const steps = [
    { id: 1, label: 'Location' },
    { id: 2, label: 'Session' },
    { id: 3, label: 'Schedule' },
    { id: 4, label: 'Add ons' },
    { id: 5, label: 'Confirm' },
  ];

  return (
    <div className="min-h-screen bg-[#fcfcfc] flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 md:px-8 pt-6">
        <SecondaryButton
          onClick={() => {
            // navigate(-1) relied on browser history, which is
            // unreliable here: Stripe redirects, sign-in detours, and
            // hard refreshes leave the previous entry pointing at /booking
            // step 1 — sending the customer back into a fresh wizard
            // that reads "Choose your cleaning session" with the wrong
            // category fallback. Route deterministically based on the
            // draft's mode instead so Back always returns to a page that
            // matches what they're checking out.
            //   • Subscription → resume the wizard at step 12 (backup pro
            //     choice), the last meaningful step before checkout.
            //   • One-off → /your-appointment summary so they can adjust
            //     date / time / wallet / coupon before paying.
            if (draft?.mode === 'SUBSCRIPTION') {
              navigate('/booking', { state: { sessionStep: 12 } });
            } else {
              navigate('/your-appointment');
            }
          }}
          icon={
            <span className="material-icons text-[18px]">navigate_before</span>
          }
        >
          Back
        </SecondaryButton>
      </div>

      <main className="max-w-[1140px] mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1">
            <div className="text-center mb-12">
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
                Continue booking
              </h1>
              <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
                {draft?.mode === 'SUBSCRIPTION'
                  ? 'Set up your recurring payment'
                  : 'Pay securely via Stripe to confirm your booking'}
              </p>
            </div>

            <div className="flex items-center justify-center mb-12 w-full px-2">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className="w-[24px] h-[24px] rounded-full flex items-center justify-center mb-2 bg-[#8e2a2f]">
                      <div className="w-[8px] h-[8px] bg-white rounded-full" />
                    </div>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] md:text-sm leading-[18px] md:leading-[21px] tracking-[-0.1px] whitespace-nowrap font-bold">
                      {step.label}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-4 sm:w-12 md:w-20 h-[2px] bg-[#8e2a2f] mb-[24px] md:mb-[29px] mx-1 shrink-0" />
                  )}
                </div>
              ))}
            </div>

            <div className="max-w-[540px] mx-auto w-full">
              <div className="flex flex-col gap-8">
                {/* Stripe illustration removed 2026-06-04 to match the
                    one-off flow's confirm step (YourAppointmentPage),
                    which surfaces the Stripe handoff via the small
                    note below + the PrimaryButton label only — no
                    giant logo placeholder. */}

                <div className="text-center">
                  <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] font-bold mb-1">
                    Free cancellation per our{' '}
                    <Link
                      to="/cancellation-policy"
                      className="underline hover:text-[#8e2a2f]"
                    >
                      cancellation policy
                    </Link>
                    .
                  </p>
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                    By tapping the button below you'll be redirected to Stripe
                    to complete payment securely. Returning here finalises your
                    booking.
                  </p>
                </div>

                <PrimaryButton
                  onClick={handlePayClick}
                  disabled={submitting || !catalogReady}
                  className="w-full py-4 mt-4"
                >
                  {submitting
                    ? 'Redirecting to Stripe…'
                    : draft?.mode === 'SUBSCRIPTION'
                      ? 'Start subscription'
                      : 'Book and Pay'}
                </PrimaryButton>
              </div>
            </div>
          </div>

          <BookingBasket
            price={basketPrice}
            duration={basketDuration}
            hasPets={basketHasPets}
            // Only flows that asked the pets question stamp meta.has_pets
            // (cleaning). Hide the pets line for the rest.
            showPets={typeof draft?.meta?.has_pets === 'string'}
            appointmentDate={basketAppointmentDate}
            appointmentTime={basketAppointmentTime}
            selectedTimes={basketTimes}
            // "Other times you've kept open" is cleaning-only — hide it for
            // hair, beauty & wellness, and childcare.
            isCleaning={
              String(
                resolved?.category?.code ?? draft?.meta?.category_code ?? '',
              ).toUpperCase() === 'CLEANING'
            }
            serviceName={resolved?.service?.name}
            categoryName={basketCategoryName}
            onSubscribeClick={() => setIsSubscriptionOpen(true)}
            // Final-step value so the basket renders the full breakdown
            // (duration line, pets line, add-ons, total).
            sessionStep={12}
            selectedAddons={basketAddons}
            addonsCatalog={addonsCatalog}
            commercialSpaceType={basketCommercialSpace}
            commercialSpaceOptions={basketCommercialOptions}
            otherCommercialSpace={basketOtherCommercial}
            pricingUnit={resolved?.service?.pricing_unit ?? 'PER_HOUR'}
            isSubscription={draft?.mode === 'SUBSCRIPTION'}
            quoteItems={draft?.items?.length ? draft.items : undefined}
            onQuoteChange={(q) =>
              setCheckoutTotal(
                q?.total_amount != null ? Number(q.total_amount) : null,
              )
            }
            walletAvailable={walletBalance}
            walletCurrency={walletCurrency}
            walletAmount={walletAmount}
            walletEnabled={walletEnabled}
            onToggleWallet={(enabled) => {
              setWalletEnabled(enabled);
              if (!enabled) {
                setWalletAmount(0);
                patchBookingDraft({ wallet_amount: 0 });
              }
            }}
            onWalletAmountChange={(n) => {
              setWalletAmount(n);
              setWalletEnabled(n > 0);
              patchBookingDraft({ wallet_amount: n });
            }}
          />
        </div>
      </main>

      <SubscriptionPlanOffcanvas
        isOpen={isSubscriptionOpen}
        onClose={() => setIsSubscriptionOpen(false)}
        onPickPlan={handlePickPlan}
      />

      <Footer />
    </div>
  );
}

function defaultScheduledStart(): string {
  // 7 days from now at 11:00 local — sensible default for a customer
  // who never picked a time during the mock wizard.
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(11, 0, 0, 0);
  return d.toISOString();
}

